import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

/** Strip inline `<i …>…</i>` annotation tags injected by the rich-text editor. */
export const removeCommentTags = (text: string): string => {
	const regex =
		/<i(?=\s)(?!(?:[^>"\\']|"[^"]*"|\\'[^\\']*\\')*?(?<=\s)(?:term|range)\s*=)(?!\s*>)\s+(?:".*?"|\\'.*?\\'|[^>]*?)+>|<\/i>/gm;

	return text.replace(regex, '');
};

/** Plain text key for comparing rich-text fields (e.g. outcomes, unit skills). */
export function plainTextKeyForCompare(raw: string): string {
	const withoutComments = removeCommentTags(raw);
	const doc = new DOMParser().parseFromString(withoutComments, 'text/html');
	const text = doc.body.textContent ?? '';
	return text.replace(/\s+/g, ' ').trim().toLowerCase();
}

/** Dedupe key for unit sub-items (skill type + normalized text). */
export function skillCompositeKey(skillType: string, skillText: string): string {
	return `${skillType}|${plainTextKeyForCompare(skillText)}`;
}

/** Keep the first item for each course_id and drop later duplicates. */
export function uniqueByCourseId<T extends { course_id?: string | null }>(items: T[]): T[] {
	const seen = new Set<string>();
	return items.filter((item) => {
		const id = item.course_id;
		if (!id || seen.has(id)) return false;
		seen.add(id);
		return true;
	});
}

/** Sort focuses_skills arrays within courses_focuses by skill_order. Mutates in place. */
export function sortFocusSkillsByOrder(
	coursesFocuses:
		| { focus: { focuses_skills: { skill_order: number }[] } | null }[]
		| null
		| undefined
) {
	if (!coursesFocuses) return;
	for (const cf of coursesFocuses) {
		cf.focus?.focuses_skills?.sort((a, b) => a.skill_order - b.skill_order);
	}
}
